import React from 'react';
import { Phone, FileText, CheckCircle, DollarSign, AlertTriangle, ClipboardCheck } from 'lucide-react';
export function CdlTypesPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2374">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2375">
        <div className="container mx-auto px-4" data-id="element-2376">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2377">
            CDL Tests Information
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2378">
            Knowledge Tests, Skills Tests & Requirements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2379">
        <div className="max-w-4xl mx-auto" data-id="element-2380">
          {/* What Tests Do I Need */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2381">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2382">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2383" />
              What Tests Do I Need To Take?
            </h2>

            <div className="space-y-6" data-id="element-2384">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2385">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2386">
                  Original (First) CDL Applicants:
                </h3>
                <p className="text-gray-700" data-id="element-2387">
                  All drivers applying for their original commercial driver
                  license must take and pass the appropriate CDL{' '}
                  <strong data-id="element-2388">knowledge (written) tests</strong> and a{' '}
                  <strong data-id="element-2389">skills test</strong>.
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2390">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2391">
                  Drivers with CDL from Another State:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2392">
                  <li data-id="element-2393">
                    • Must always take the{' '}
                    <strong data-id="element-2394">H-Hazardous Materials</strong> endorsement written
                    test to keep the H-endorsement
                  </li>
                  <li data-id="element-2395">
                    • Must pass appropriate knowledge tests when changing group
                    designator, removing restrictions, or adding endorsements
                  </li>
                  <li data-id="element-2396">
                    • May need to take CDL skills tests if upgrading group
                    designator, removing restrictions, or adding P (Passenger)
                    or S (School Bus) endorsement
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Knowledge Tests Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2397">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2398">
              Knowledge Tests
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2399">
              You will take one or more knowledge tests. The following chart
              summarizes the type of tests and number of questions required
              depending on the type of vehicle you drive and the type of cargo
              you haul.
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mb-6" data-id="element-2400">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2401">
                CDL Tests by Group
              </h3>

              <div className="space-y-4" data-id="element-2402">
                <div className="bg-white rounded-lg p-4" data-id="element-2403">
                  <div className="flex justify-between items-center" data-id="element-2404">
                    <div data-id="element-2405">
                      <p className="font-bold text-blue-600 text-xl" data-id="element-2406">Group A</p>
                      <p className="text-sm text-gray-600" data-id="element-2407">
                        Includes 20-question combination vehicle test
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2408">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2409">
                        70 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2410">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2411">
                  <div className="flex justify-between items-center" data-id="element-2412">
                    <div data-id="element-2413">
                      <p className="font-bold text-green-600 text-xl" data-id="element-2414">
                        Group B
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2415">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2416">
                        50 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2417">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2418">
                  <div className="flex justify-between items-center" data-id="element-2419">
                    <div data-id="element-2420">
                      <p className="font-bold text-yellow-600 text-xl" data-id="element-2421">
                        Group C
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2422">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2423">
                        50 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2424">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6" data-id="element-2425">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2426">Endorsements</h3>

              <div className="space-y-3" data-id="element-2427">
                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2428">
                  <div className="flex items-center" data-id="element-2429">
                    <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2430">
                      T
                    </span>
                    <span className="font-semibold" data-id="element-2431">Double Trailer</span>
                  </div>
                  <div className="text-right" data-id="element-2432">
                    <p className="font-bold" data-id="element-2433">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2434">Knowledge only</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2435">
                  <div className="flex items-center" data-id="element-2436">
                    <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2437">
                      P
                    </span>
                    <span className="font-semibold" data-id="element-2438">Passenger</span>
                  </div>
                  <div className="text-right" data-id="element-2439">
                    <p className="font-bold" data-id="element-2440">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2441">Knowledge + Skills</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2442">
                  <div className="flex items-center" data-id="element-2443">
                    <span className="bg-yellow-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2444">
                      S
                    </span>
                    <span className="font-semibold" data-id="element-2445">School Bus</span>
                  </div>
                  <div className="text-right" data-id="element-2446">
                    <p className="font-bold" data-id="element-2447">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2448">Knowledge + Skills</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2449">
                  <div className="flex items-center" data-id="element-2450">
                    <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2451">
                      N
                    </span>
                    <span className="font-semibold" data-id="element-2452">Tank Vehicles</span>
                  </div>
                  <div className="text-right" data-id="element-2453">
                    <p className="font-bold" data-id="element-2454">30 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2455">Knowledge only</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2456">
                  <div className="flex items-center" data-id="element-2457">
                    <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2458">
                      H
                    </span>
                    <span className="font-semibold" data-id="element-2459">Hazardous Materials</span>
                  </div>
                  <div className="text-right" data-id="element-2460">
                    <p className="font-bold" data-id="element-2461">30 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2462">Knowledge only</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mt-6" data-id="element-2463">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2464">Air Brakes Test</h3>
              <p className="text-gray-700 mb-3" data-id="element-2465">
                If you want to be licensed to drive vehicles with air brakes,
                you must pass a{' '}
                <strong data-id="element-2466">25-question air brake knowledge test</strong>. The
                vehicle you use for the skills test must be equipped with air
                brakes.
              </p>
              <p className="text-sm text-gray-600" data-id="element-2467">
                If either of these conditions has not been met, you will be
                restricted from operating vehicles with air brakes.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-2468">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2469">
                Test Format & Passing Score:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2470">
                <li className="flex items-start" data-id="element-2471">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2472" />
                  <span data-id="element-2473">
                    All CDL knowledge tests are administered in written form
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2474">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2475" />
                  <span data-id="element-2476">
                    Minimum passing score of <strong data-id="element-2477">80%</strong> is required to
                    pass each test
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2478">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2479" />
                  <span data-id="element-2480">
                    Oral tests available on audiocassettes for limited reading
                    skills (except H-Hazardous Materials and S-School Bus)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2481">
                  <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2482" />
                  <span data-id="element-2483">
                    H-Hazardous Materials and S-School Bus endorsements must
                    pass written knowledge test
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* CDL Skills Test */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2484">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2485">
              <ClipboardCheck className="mr-3 text-[#B1050B]" size={28} data-id="element-2486" />
              CDL Skills Test
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2487">
              You will need to pass the CDL skills test which includes three
              parts:
            </p>

            <div className="space-y-4 mb-6" data-id="element-2488">
              <div className="bg-blue-50 rounded-lg p-6 flex items-start" data-id="element-2489">
                <span className="bg-blue-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2490">
                  1
                </span>
                <div data-id="element-2491">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2492">
                    Vehicle Inspection Test
                  </p>
                  <p className="text-gray-700" data-id="element-2493">
                    To measure your ability to perform a vehicle safety check. A
                    Vehicle Inspection Memory Aid is available and may be used
                    during the test.
                  </p>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6 flex items-start" data-id="element-2494">
                <span className="bg-green-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2495">
                  2
                </span>
                <div data-id="element-2496">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2497">
                    Off-Street Basic Control Skills Test
                  </p>
                  <p className="text-gray-700" data-id="element-2498">
                    Testing your ability to control the vehicle in various
                    maneuvers
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6 flex items-start" data-id="element-2499">
                <span className="bg-yellow-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2500">
                  3
                </span>
                <div data-id="element-2501">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2502">
                    On-Street Driving Test
                  </p>
                  <p className="text-gray-700" data-id="element-2503">
                    Testing your ability to drive safely in traffic
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6" data-id="element-2504">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2505">
                Important Testing Rules:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2506">
                <li data-id="element-2507">
                  • All three parts are conducted during the same testing
                  session, in the order listed above
                </li>
                <li data-id="element-2508">
                  • Each portion must be passed before continuing to the next
                </li>
                <li data-id="element-2509">• A failure of any segment terminates testing</li>
                <li data-id="element-2510">
                  • On subsequent attempts, you must always start with the
                  vehicle inspection regardless of which part you previously
                  failed
                </li>
                <li data-id="element-2511">
                  • You are allowed <strong data-id="element-2512">one (1) attempt per day</strong>
                </li>
                <li data-id="element-2513">
                  • The Secretary of State has the right to randomly retest
                  anyone tested by a third party tester
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6" data-id="element-2514">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2515">
                Vehicle & Documentation Requirements:
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-2516">
                <li className="flex items-start" data-id="element-2517">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2518" />
                  <span data-id="element-2519">
                    You must supply an empty vehicle with a GVWR representative
                    of the type you plan to drive (including a trailer if
                    applicable)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2520">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2521" />
                  <span data-id="element-2522">
                    The vehicle must be equipped with at least two permanently
                    mounted seats for yourself and the examiner
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2523">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2524" />
                  <span data-id="element-2525">
                    Safety belts are required for both driver and examiner
                    (except for examiner on buses)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2526">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2527" />
                  <span data-id="element-2528">
                    Applicants applying for P-Passenger endorsement must pass
                    skills test in a vehicle with seating capacity of{' '}
                    <strong data-id="element-2529">16 or more passengers</strong>, including the driver
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2530">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2531" />
                  <span data-id="element-2532">
                    You must furnish: Michigan photo driver license, CDL
                    Temporary Instruction Permit (TIP), and Medical Examiner's
                    Certificate (DOT card, School Bus card) or medical waiver
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* CDL Restrictions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2533">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2534">
              <AlertTriangle className="mr-3 text-yellow-600" size={28} data-id="element-2535" />
              What CDL Restrictions Could I Receive?
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2536">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2537">
                Restriction Code 30
              </h3>
              <p className="text-gray-700 mb-3" data-id="element-2538">
                <strong data-id="element-2539">
                  "CDL-P or S endorsement valid in Group C vehicles only."
                </strong>
              </p>
              <ul className="space-y-2 text-gray-700 ml-4" data-id="element-2540">
                <li data-id="element-2541">
                  • Adding a P-Passenger endorsement to your current Group A or
                  B designation and completing your CDL skills test in a Group C
                  bus will limit the P-Passenger endorsement to operation of a
                  Group C bus
                </li>
                <li data-id="element-2542">
                  • Adding P-Passenger and S-School Bus endorsements to your
                  current Group A or B designation and completing your CDL
                  skills test in a school bus will limit both endorsements to
                  operation of Group C school buses
                </li>
              </ul>
            </div>
          </div>

          {/* Seasonal Restricted CDL */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2543">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2544">
              Seasonal Restricted CDL
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2545">
              A special seasonal restricted CDL is available for agri-business
              employees operating Group B and C vehicles on routes within{' '}
              <strong data-id="element-2546">150 miles</strong> from the place of business.
            </p>

            <div className="space-y-4" data-id="element-2547">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2548">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2549">Requirements:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2550">
                  <li data-id="element-2551">
                    • Buses and school buses cannot be operated with this
                    restricted license
                  </li>
                  <li data-id="element-2552">• CDL knowledge and skills tests are NOT required</li>
                  <li data-id="element-2553">
                    • Applicants must have held a valid driver license for at
                    least <strong data-id="element-2554">one year</strong>
                  </li>
                  <li data-id="element-2555">
                    • Must possess a clean driving record for the{' '}
                    <strong data-id="element-2556">three-year period</strong> preceding application
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2557">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2558">
                  Validity Period:
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-2559">
                  This limited CDL must be applied for each calendar year.
                  Within a one-year period, applicants can choose:
                </p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2560">
                  <li data-id="element-2561">
                    • Valid for <strong data-id="element-2562">180 days</strong> from date of issue, OR
                  </li>
                  <li data-id="element-2563">
                    • Valid for seasonal periods:{' '}
                    <strong data-id="element-2564">April 2 - June 30</strong> and{' '}
                    <strong data-id="element-2565">September 2 - November 30</strong>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2566">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2567">
                  Permitted Hazardous Materials (without H or X endorsements):
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2568">
                  <li data-id="element-2569">
                    • Diesel motor fuel in quantities of{' '}
                    <strong data-id="element-2570">1,000 gallons or less</strong>
                  </li>
                  <li data-id="element-2571">
                    • Liquid fertilizers in quantities of{' '}
                    <strong data-id="element-2572">3,000 gallons or less</strong>
                  </li>
                  <li data-id="element-2573">
                    • Solid fertilizers that are not transported with any
                    organic substance
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* License Fees */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2574">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-2575">
              <DollarSign className="mr-3" size={32} data-id="element-2576" />
              What License Fees Do I Pay?
            </h2>

            <div className="space-y-4" data-id="element-2577">
              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2578">
                <span className="font-semibold" data-id="element-2579">Base Operator</span>
                <span className="text-2xl font-bold" data-id="element-2580">$25</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2581">
                <span className="font-semibold" data-id="element-2582">Base Chauffeur</span>
                <span className="text-2xl font-bold" data-id="element-2583">$35</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-2584">
                <p className="text-center font-bold mb-2" data-id="element-2585">PLUS</p>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2586">
                <span className="font-semibold" data-id="element-2587">
                  CDL Vehicle Group Designation
                </span>
                <span className="text-2xl font-bold" data-id="element-2588">$25</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-2589">
                <div className="flex justify-between items-center mb-2" data-id="element-2590">
                  <span className="font-semibold" data-id="element-2591">
                    CDL Endorsements (if any)
                  </span>
                  <span className="text-2xl font-bold" data-id="element-2592">$5 each</span>
                </div>
                <p className="text-sm text-gray-200 text-right" data-id="element-2593">
                  $10 for "X" Endorsement
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2594">
                <span className="font-semibold" data-id="element-2595">
                  Correction Fee (if applicable)
                </span>
                <span className="text-2xl font-bold" data-id="element-2596">$18</span>
              </div>
            </div>
          </div>

          {/* Third Party Testing */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2597">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2598">
              Third Party Driver Skills Test Fee Requirements
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2599">
              <p className="text-gray-800" data-id="element-2600">
                Third party driver skills test fees and refund policies vary. Be
                sure to discuss and understand your test fees and scheduling
                requirements with the third party organization that you have
                selected before taking your test.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2601">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2602">
              Need Help with CDL Testing?
            </h2>
            <p className="text-xl mb-6" data-id="element-2603">
              Expert guidance on CDL tests, requirements, and preparation
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2604">
              <Phone className="mr-3" size={24} data-id="element-2605" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2606">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}